import { useNavigate, useParams } from "react-router"
import { motion } from "motion/react"
import { toast } from "sonner"
import { Building07, UserCheck01, AlertCircle, CheckCircle } from "@untitledui/icons"

import { useInvite, useAcceptInvite } from "@/lib/api/use-invites"
import { useAuth } from "@/lib/auth/auth-context"
import { api, ApiError } from "@/lib/api/client"
import type { ApiUserWithTeams } from "@/lib/api/users"
import { WelcomeShell, WelcomeCard } from "@/components/welcome/welcome-shell"
import { PrimaryButton } from "@/components/onboarding/onboarding-shell"
import type { WelcomeNavState } from "@/lib/welcome/nav-state"

function fullName(p: { firstName: string; lastName: string }, fallback: string) {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ")
  return name || fallback
}

/**
 * Build the navigation state that drives the personalised welcome chain.
 * The public-invites endpoint returns a slim `team: { name }` shape (no id,
 * no type), so we look up the just-joined team in the refreshed user's
 * /auth/profile teams list by name match. If the lookup fails — race against
 * profile cache, name renamed concurrently, etc. — the welcome chain falls
 * back to its no-state degraded mode (generic copy, no team badge), which
 * is still functional.
 */
function deriveNavState(
  apiUser: ApiUserWithTeams,
  invite: { team: { name: string }; role: string },
): WelcomeNavState | null {
  const team = apiUser.teams.find((t) => t.name === invite.team.name)
  if (!team) return null
  return {
    teamId: team.id,
    teamName: team.name,
    teamType: team.type,
    role: invite.role,
  }
}

/**
 * /invites/:uuid — accept-invite preview rendered inside WelcomeShell so the
 * chrome stays continuous from the invite link through the full /welcome/*
 * wizard. Two outbound paths after a successful accept:
 *   1. New user (BE returned a one-shot bearer token) → /welcome/password
 *      with team/role state captured from the post-accept profile.
 *   2. Existing user signed in as the invited email → /welcome/role-tour
 *      with the same state. Different signed-in email → /login (no flow).
 */
export default function InvitePage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { user, refreshUser } = useAuth()

  const { data: invite, isLoading, error } = useInvite(uuid)
  const accept = useAcceptInvite()

  const status = (error as ApiError | undefined)?.status

  if (isLoading) {
    return (
      <WelcomeShell step="password" hideProgress>
        <WelcomeCard title="Loading your invite…">
          <div className="space-y-3 animate-pulse">
            <div className="h-5 w-48 rounded bg-zinc-100" />
            <div className="h-4 w-64 rounded bg-zinc-100" />
            <div className="h-4 w-40 rounded bg-zinc-100" />
          </div>
        </WelcomeCard>
      </WelcomeShell>
    )
  }

  if (error || !invite) {
    // 403 from the real BE means already-accepted; group it with 404/410 as "gone".
    const isGone = status === 404 || status === 410 || status === 403
    return (
      <WelcomeShell step="password" hideProgress>
        <WelcomeCard
          title={isGone ? "This invite is no longer valid" : "Couldn't load invite"}
          subtitle={
            isGone
              ? "It may have been revoked, already accepted, or never existed."
              : "Something went wrong fetching this invite. Try again, or contact whoever sent it."
          }
        >
          <div className="flex justify-start">
            <div className="size-12 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
              <AlertCircle className="size-6 text-zinc-500" aria-hidden />
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="block w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-semibold hover:bg-black transition-colors"
          >
            Go home
          </button>
        </WelcomeCard>
      </WelcomeShell>
    )
  }

  const handleAccept = async () => {
    try {
      const result = await accept.mutateAsync(invite.uuid)
      if (result.status !== "success") {
        toast.error("Couldn't accept invite. It may have been revoked.")
        return
      }

      if (result.token) {
        // BE auto-created the user (hasSetPassword=false) and returned a JWT
        // scoped to /auth/set-password. Persist + refresh so we can pick the
        // just-joined team out of /auth/profile, then route to /welcome/password
        // with full nav state.
        api.setToken(result.token)
        let navState: WelcomeNavState | null = null
        try {
          const apiUser = await refreshUser()
          navState = deriveNavState(apiUser, invite)
        } catch {
          // refreshUser failing is non-fatal — the token is stored, so
          // /welcome/password will render and authenticate. We just won't
          // have the team badge in the chrome.
        }
        navigate("/welcome/password", { replace: true, state: navState })
        return
      }

      const matchesCurrentUser =
        !!user?.email && user.email.toLowerCase() === invite.email.toLowerCase()
      if (matchesCurrentUser) {
        // Existing signed-in user accepted on their own account — refresh so
        // /auth/profile picks up the new membership, then jump straight to
        // the role-tour with the captured nav state. Skip /welcome/password
        // (they already have a password) and /welcome/profile (their name is
        // already on the user record).
        let navState: WelcomeNavState | null = null
        try {
          const apiUser = await refreshUser()
          navState = deriveNavState(apiUser, invite)
        } catch {
          // Non-fatal; tour falls through to / on missing nav state.
        }
        navigate("/welcome/role-tour", { replace: true, state: navState })
      } else {
        toast.success(`You've joined ${invite.team.name} — sign in to access it`)
        navigate("/login", { state: { prefillEmail: invite.email } })
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't accept invite")
    }
  }

  const inviterName = fullName(invite.inviter, "your teammate")

  return (
    <WelcomeShell
      step="password"
      hideProgress
      team={{ name: invite.team.name, role: invite.role }}
    >
      <WelcomeCard
        eyebrow="You've been invited"
        title={`Join ${invite.team.name}.`}
        subtitle={`Sent to ${invite.email}.`}
        hero={<InviteHero teamName={invite.team.name} role={invite.role} />}
      >
        <div className="space-y-5">
          <div className="space-y-3 rounded-2xl bg-zinc-50 p-4">
            <Row icon={<UserCheck01 className="size-4 text-zinc-400" aria-hidden />} label="Invited by">
              {inviterName}
            </Row>
            <Row icon={<Building07 className="size-4 text-zinc-400" aria-hidden />} label="Role">
              {invite.role}
            </Row>
            {invite.department && (
              <Row icon={<Building07 className="size-4 text-zinc-400" aria-hidden />} label="Department">
                {invite.department}
              </Row>
            )}
          </div>

          {user && user.email.toLowerCase() !== invite.email.toLowerCase() && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800"
            >
              You're signed in as <span className="font-medium">{user.email}</span>, but
              this invite is for <span className="font-medium">{invite.email}</span>.
              After accepting, you'll be asked to sign in with the right account.
            </motion.div>
          )}

          <PrimaryButton
            type="button"
            onClick={handleAccept}
            loading={accept.isPending}
            size="wide"
            variant="dark"
          >
            Accept invitation
          </PrimaryButton>

          {!user && (
            <p className="text-xs text-zinc-400 text-center">
              New here? You'll set your password on the next step.
            </p>
          )}
        </div>
      </WelcomeCard>
    </WelcomeShell>
  )
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="text-zinc-400 w-20 shrink-0">{label}</span>
      <span className="text-zinc-900 font-medium break-words min-w-0 flex-1">{children}</span>
    </div>
  )
}

function InviteHero({ teamName, role }: { teamName: string; role: string }) {
  return (
    <div className="w-full max-w-sm space-y-5">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          You're joining
        </p>
        <h3 className="mt-2 text-[26px] font-bold text-zinc-900 tracking-tight leading-tight">
          {teamName}
        </h3>
        <p className="mt-1 text-[14px] text-zinc-500">as {role}</p>
      </div>

      <div className="space-y-2.5">
        {[
          "One click to accept.",
          "Pick a password if you're new.",
          "Confirm your name on the next step.",
        ].map((line, i) => (
          <div key={i} className="flex items-start gap-2.5 text-[13px] text-zinc-600">
            <CheckCircle className="mt-0.5 size-4 text-emerald-500 shrink-0" aria-hidden />
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
